import { create } from 'zustand'
import type { NavKey } from './nav'
import { ROOT_TABS, ROUTES } from './nav'

/** Where "‹ Back" goes on a pushed screen. */
export interface BackTarget {
  /** The destination. Omit to go back in history (falling back to the last root tab). */
  to?: string
  /** The destination's name, shown after the chevron: "‹ Feed controls". */
  label?: string
}

export interface ChromeState {
  title: string
  navKey?: NavKey
  group?: string
  back: BackTarget | null
  /** false hides the phone bars (an immersive screen such as Record). */
  bars: boolean
  /** Bumped by each PageChrome mount, so a late cleanup never wipes the next screen's chrome. */
  owner: number
  /** The last root tab visited, for a Back with no label of its own. */
  lastRoot: { to: string; label: string }
  /** Portal targets for a screen's actions, registered by the bars. */
  phoneActions: HTMLElement | null
  deskActions: HTMLElement | null
  /** The seed panel (the chip's on the phone, the meter's on desktop). Settings'
   *  "What uses seeds" and Emma's "Tap for your seeds" open it too. */
  seedPanel: boolean
  /** Record ＋'s "Choose a story to record" sheet, over whatever screen is open (rec-pick.js). */
  recPick: boolean
  set: (patch: Partial<ChromeState>) => void
}

function readRoot(): { to: string; label: string } {
  try {
    const raw = sessionStorage.getItem('gg.root')
    if (raw) return JSON.parse(raw)
  } catch { /* private mode */ }
  return { to: ROUTES.feed, label: ROOT_TABS.feed!.back }
}

export const useChrome = create<ChromeState>((set) => ({
  title: '',
  back: null,
  bars: true,
  owner: 0,
  lastRoot: readRoot(),
  phoneActions: null,
  deskActions: null,
  seedPanel: false,
  recPick: false,
  set: (patch) => set(patch),
}))

export function rememberRoot(navKey: NavKey | undefined) {
  const r = navKey ? ROOT_TABS[navKey] : undefined
  if (!r) return
  const lastRoot = { to: r.to, label: r.back }
  try { sessionStorage.setItem('gg.root', JSON.stringify(lastRoot)) } catch { /* private mode */ }
  useChrome.getState().set({ lastRoot })
}

/** Opens or closes the seed panel from anywhere. */
export function setSeedPanel(open: boolean) {
  useChrome.getState().set({ seedPanel: open })
}

/** Opens or closes Record ＋'s sheet over the current screen. */
export function setRecPick(recPick: boolean) {
  useChrome.getState().set({ recPick })
}
